/*
 * Module for handling the intent example editor
 */

// noinspection JSUnusedLocalSymbols
define([
  "marionette",
  "messaging/messenger",
  "modules/topicsSplit/topic/topicExampleModal/exampleEditor/exampleEditorView",
  "modules/topicsSplit/topic/topicExampleModal/exampleEditor/exampleEditorModel",
], function (Marionette, Messenger, ExampleEditorView) {
  return Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "exampleEditor";
      //boolean describing whether the optional or substitution buttons are selected
      this.optionalMode = false;
      this.subMode = false;
      this.lastEvaluatedExample = "";
    },
    show: function (example, startingExampleString) {
      this.example = example;
      this.exampleEntries = example.entries();
      // Get the locale for the selected example. If it's not English, then hide the optional and substitution buttons
      const selectedLocale = Messenger.reqres.request("topic:example:modal:locale");
      const isEnglish = Messenger.reqres.request("kb:language:is:english", selectedLocale);
      // Update strength about every 2 seconds. DO NOT USE window.setInterval, because if the server is slow
      // this can stack up server requests which is very, very bad.
      this.view = new ExampleEditorView.exampleEditorView({ collection: this.exampleEntries });
      this.view.once(
        "close",
        function () {
          this.stopStrengthPoll();
        },
        this,
      );
      this._setUpView();
      this.showRegion(this.view);
      //if the textbox should have a starting example string, trigger a text:changed event and pass the starting text
      if (startingExampleString) {
        //updates the example model
        this.view.trigger("text:changed", startingExampleString, startingExampleString.length);
        //rerender to see the example in the textarea
        this.view.render();
      }
      //update strength
      this.startStrengthPoll();
      if (!isEnglish) {
        Messenger.vent.trigger("hide:example:buttons", true);
      } else {
        Messenger.vent.trigger("hide:translate:button", true);
      }
    },
    hide: function (isHidden) {
      this.view.hideButtons(isHidden);
    },
    hideTranslateButton: function (isHidden) {
      this.view.hideTranslateButton(isHidden);
    },
    startStrengthPoll: function () {
      if (!this.intervalId) {
        this._intervalFunction();
      }
    },
    stopStrengthPoll: function () {
      if (this.intervalId) {
        window.clearTimeout(this.intervalId);
        this.intervalId = 0;
      }
    },
    updateText: function () {
      this.view.updateText();
    },
    removeExtraWhitespace: function () {
      this.view.removeExtraWhitespace();
    },
    /* Function which adds the new text into the example's collection
     * insertPosition: is the string index where the text was added
     * addedText: is the text which was added
     */
    _manageAddedText: function (insertPosition, addedText) {
      //find model where the added text belongs
      let i;
      let index = 0;
      let modifiedEntryPosition = 0; //position of the modified example entry
      let modifiedEntryOffset = 0; //the offset, from the start of the entry, where the insert began
      for (i = 0; i < this.exampleEntries.length; i++) {
        const entryLength = this.exampleEntries.at(i).get("surfaceForm").length; //get the length of the i'th example entry
        if (index + entryLength >= insertPosition) {
          modifiedEntryPosition = i;
          modifiedEntryOffset = insertPosition - index;
          break;
        }
        index += entryLength + 1; //add 1 for the space between entries
      }
      //modify the entry with the text, creating a new entry each time there's a space
      const entryText = this.exampleEntries.at(modifiedEntryPosition).get("surfaceForm");
      let textBeforeOffset = entryText.slice(0, modifiedEntryOffset);
      const textAfterOffset = entryText.slice(modifiedEntryOffset);
      for (i = 0; i < addedText.length; i++) {
        if (addedText.charAt(i) === " ") {
          //space found, set changes to current entry and add a new one
          this.exampleEntries.at(modifiedEntryPosition).set("surfaceForm", textBeforeOffset);
          modifiedEntryPosition++;
          const newEntry = this.exampleEntries.addEntry(modifiedEntryPosition);
          textBeforeOffset = newEntry.get("surfaceForm");
        } else {
          textBeforeOffset += addedText.charAt(i);
        }
      }
      //apply any text after the offset
      this.exampleEntries
        .at(modifiedEntryPosition)
        .set("surfaceForm", textBeforeOffset + textAfterOffset);
    },
    _intervalFunction: function () {
      this.intervalId = 0;
      Messenger.vent.once(
        "strength:updated",
        function () {
          this.intervalId = this.setTimeout(this._intervalFunction.bind(this), 2000);
        },
        this,
      );
      this.view.updateText();
      Messenger.vent.trigger("update:strength", this.view.collection);
    },
    /* Function which removes text from the example's collection
     * removalStartPosition: is the string index where the text removed from
     * countOfCharsRemoved: is the number of characters removed, starting at removalStartPosition
     */
    _manageRemovedText: function (removalStartPosition, countOfCharsRemoved) {
      //find model where the added text belongs
      let entryCount;
      let index = 0;
      let modifiedEntryStartPosition = 0; //position of the first modified example entry
      let modifiedEntryEndPosition = 0; //position of the last modified example entry
      let modifiedEntryStartOffset = 0; //the offset, from the start of the entry, where the removal began
      let modifiedEntryEndOffset = 0; //offset, from the start of the entry, where the removal ended
      let modifiedEntryStartText; //text remaining in the first modified example entry
      let modifiedEntryEndText; //text remaining in the last modified example entry
      let startPosFound = false;
      //find the above values by iterating through the entries collection
      for (let i = 0; i < this.exampleEntries.length; i++) {
        const entryText = this.exampleEntries.at(i).get("surfaceForm");
        const entryLength = entryText.length; //get the length of the i'th example entry
        if (!startPosFound && index + entryLength >= removalStartPosition) {
          modifiedEntryStartPosition = i;
          modifiedEntryStartOffset = removalStartPosition - index;
          modifiedEntryStartText = entryText.slice(0, modifiedEntryStartOffset);
          startPosFound = true;
        }
        if (index + entryLength >= removalStartPosition + countOfCharsRemoved) {
          modifiedEntryEndPosition = i;
          modifiedEntryEndOffset = removalStartPosition + countOfCharsRemoved - index;
          modifiedEntryEndText = entryText.slice(modifiedEntryEndOffset);
          break;
        }
        index += entryLength + 1; //add 1 for the space between entries
      }
      /**
       * case 1: if the modifiedEntryStartOffset and modifiedEntryEndOffset both equal zero,
       * then that means we are splicing out one or more example entries completely.  We should start
       * at modifiedEntryStartPosition and delete until we get to modifiedEntryEndPosition (but don't delete it
       * because nothing happened to it)
       */
      if (modifiedEntryStartOffset === 0 && modifiedEntryEndOffset === 0) {
        for (
          entryCount = modifiedEntryStartPosition;
          entryCount < modifiedEntryEndPosition;
          entryCount++
        ) {
          //BIG NOTE: we always remove the entry with sequenceOrder at modifiedEntryStartPosition.  This is because when we remove
          //an entry, we shift the sequence orders of the following entries down by one.  So, for example, say we needed
          //to delete entries with seqOrder 1 and 2.  First we would delete the entry with seq order 1.  When we did that, the
          //entry with seq order 2 would have it's seqOrder changed and become 1.  Therefore, when we go to delete the next entry
          //we delete the entry with seqOrder 1 again.
          const entry = this.exampleEntries.findWhere({
            sequenceOrder: modifiedEntryStartPosition,
          });
          console.log("removed entry: " + entry.get("surfaceForm"));
          this.exampleEntries.removeEntry(entry);
        }
      } else {
        /*
         * Case 2: If we are deleting part of an entry or part of multiple entries.
         * Set the surfaceForm of the first modified entry to be the
         * modifiedEntryStartText + modifiedEntryEndText and then remove any entries that were
         * in between the start entry, exclusive, and end entry, inclusive (i.e. if the delete spanned entries 8,9 and 10, then
         * we would change entry 8's surface form to be the text from 8 before the delete plus the
         * text from 10 after the delete, and we would delete entry 9 and 10.
         */
        const updatedText = modifiedEntryStartText + modifiedEntryEndText;
        this.exampleEntries.at(modifiedEntryStartPosition).set("surfaceForm", updatedText);
        for (
          entryCount = modifiedEntryStartPosition + 1;
          entryCount <= modifiedEntryEndPosition;
          entryCount++
        ) {
          //BIG NOTE (same as above): We always remove the entry with sequenceOrder one greater than the start position entry.  This is because
          //each time we remove an entry, the subsequent entries' sequenceOrders get shifted down by one.
          entry = this.exampleEntries.findWhere({ sequenceOrder: modifiedEntryStartPosition + 1 });
          this.exampleEntries.removeEntry(entry);
        }
      }
    },
    _manageTextChange: function (text, oldText, cursorPos) {
      const countOfCharsAdded = text.length - oldText.length;
      if (countOfCharsAdded > 0) {
        //see what was added
        const addedText = text.substring(cursorPos - countOfCharsAdded, cursorPos);
        this._manageAddedText(cursorPos - countOfCharsAdded, addedText);
      } else if (countOfCharsAdded < 0) {
        this._manageRemovedText(cursorPos, -countOfCharsAdded);
      }
      //else nothing changed
    },
    _setUpView: function () {
      //toggle sub group dropdown menu or toggle optional parameter
      this.view.on(
        "itemview:click:word",
        function (itemView) {
          //check which mode is active
          if (this.optionalMode) {
            //two if-statements separated for clarity...
            //if the entry has a sub group, don't allow the optional tag to be modified (maybe should notify user that this is not allowed?)
            if (itemView.model.get("substitutionGroupId") === 0) {
              //toggle the optional attribute (this will cause the view to re-render, stopping the sub group dropdown from ever appearing)
              itemView.model.set("isOptional", !itemView.model.get("isOptional"));
            }
          } else if (this.subMode) {
            //if the entry is optional, do not allow the sub group menu to be opened (once again, maybe should notify user)
            if (!itemView.model.get("isOptional")) {
              console.log("open sub group menu");
              itemView.setLoadingSpinner();
              //get matching sub groups
              const matches = Messenger.reqres.request(
                "example:entry:substitution:matches",
                itemView.model.collection,
                itemView.model.get("sequenceOrder"),
              );
              matches.once("sync", function () {
                console.log("# matches: " + matches.length);
                //insert choices into dropdown
                itemView.setSubChoices(matches);
              });
            }
          }
        },
        this,
      );
      //turn optional mode on or off
      this.view.on(
        "optional:mode",
        function () {
          this.optionalMode = !this.optionalMode; //toggle optionalMode
          if (this.subMode) {
            //deselect sub button if active
            this.subMode = false;
            this.view.toggleSubMode(false);
          }
          //set visual representation of optional button
          this.view.toggleOptionalMode(this.optionalMode);
          //either hide the text area so we can select words or show the area so we can edit text
          if (this.optionalMode) {
            this.view.hideTextArea();
          } else {
            this.view.showTextArea();
          }
        },
        this,
      );
      //turn substitution mode on or off
      this.view.on(
        "sub:mode",
        function () {
          this.subMode = !this.subMode;
          if (this.optionalMode) {
            this.optionalMode = false;
            this.view.toggleOptionalMode(false);
          }
          this.view.toggleSubMode(this.subMode);
          if (this.subMode) {
            this.view.hideTextArea();
          } else {
            this.view.showTextArea();
          }
        },
        this,
      );
      this.view.on(
        "translate",
        function () {
          this.translate();
        },
        this,
      );
      //remove the substitution group from the word
      this.view.on(
        "itemview:remove:sub",
        function (itemView) {
          //remove subgroup id and set substitutionBegin to false (and deal with multiword sub group)
          itemView.model.removeSubGroup();
        },
        this,
      );
      //add or change the sub group of a word
      this.view.on(
        "itemview:change:sub",
        function (itemView, subId, entryName) {
          subId = parseInt(subId, 10);
          //get sub group match
          const subGroupMatch = itemView.subGroups.findWhere({
            substitutionGroupId: subId,
            matchingEntry: entryName,
          });
          itemView.model.addSubGroup(subGroupMatch);
        },
        this,
      );
      //handle updating the collection when the text is edited
      this.view.on(
        "text:changed",
        function (text, cursorPos) {
          const oldText = this.view.text;
          /*
           * Special Case 1: if the user highlights text, and then types or pastes new text, then they have both removed
           * text and added new text.  We should treat this as two separate events, one where the text was removed
           * and another where the typed or pasted text is inserted.
           * We can identify this case by checking:
           * 1) If the new text is equal to or longer than the old text and removing the length difference from before the cursor position does not
           * make the new text equal to the old text (ie. something else happened to the text before the cursor beyond the increase in string length)
           * Example: old: "I walked to the store", new: "I walked to his local| store"
           * 2) If the new text is shorter than the old text and the text before the cursor does not match that of the old text
           * Example: old: "He walked quickly", new: "He walks| quickly"
           */
          const preChangeIndex = cursorPos - (text.length - oldText.length);
          if (
            (text.length >= oldText.length &&
              text.slice(0, preChangeIndex) !== oldText.slice(0, preChangeIndex)) ||
            (text.length < oldText.length &&
              text.slice(0, cursorPos) !== oldText.slice(0, cursorPos))
          ) {
            //create "intermediate" state where old text was removed by removing the added text from the text variable
            let i = 0;
            while (i < cursorPos && text.charAt(i) === oldText.charAt(i)) {
              i++;
            }
            const intermediateText = text.slice(0, i) + text.slice(cursorPos);
            const intermediateCursorPos = i;
            //split into two events, old text->intermediateText, and intermediateText->text
            console.log("event 1");
            this._manageTextChange(intermediateText, oldText, intermediateCursorPos);
            console.log("event 2");
            this._manageTextChange(text, intermediateText, cursorPos);
          }
          /*
           * Special Case 2: Dragging text from one part of the textarea to another
           * DRAGGING HAS BEEN DISABLED IN THE TEXTAREA
           */
          //          else if (text.length === oldText.length){
          //            console.log("special case 2: dragging text");
          //          }
          else {
            this._manageTextChange(text, oldText, cursorPos);
          }
        },
        this,
      );
    },
    _buildExampleString: function (exampleEntries) {
      let exampleString = "";
      exampleEntries.each(function (entry) {
        exampleString += entry.get("surfaceForm") + " ";
      }, this);
      exampleString = exampleString.trimStrict();
      return exampleString;
    },
    //check if example has been changed.  Only consider surface form string, not substitutions or optionals
    _isExampleChanged: function (oldExampleString, newExampleString) {
      return oldExampleString.toLowerCase() !== newExampleString.toLowerCase();
    },
    //user is asking for english translation
    translate: function () {
      //do something
      let exampleString = this._buildExampleString(this.exampleEntries);
      //check if there have been any differences in the example between the last time a strength was checked
      const isChange = this._isExampleChanged(this.lastEvaluatedExample, exampleString);
      if (isChange) {
        Messenger.vent.trigger("fetch:english:translation", this.example);
        this.lastEvaluatedExample = exampleString;
      }
    },
  });
});
